import React, { useState } from "react";
import {
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { superInvestors } from "./investorsConfig";
import { useSuperInvestorHoldings } from "./hooks/useSuperInvestorHoldings";
import PortfolioDistributionChart from "./PortfolioDistributionChart";
import { CompanyHoldingsTables } from "../Dashboard/Charts/components/CompanyHoldingsTables";
import { formatNumber } from "../Dashboard/Charts/utils/formatNumber";
import { hapticSelection } from "../../lib/haptics";
import { getPersonPortraitImageProps } from "./personPortrait";
import {
  InvestorWorkspace,
  WorkspaceHeader,
  WorkspaceMetric,
  WorkspaceState,
} from "./components/InvestorWorkspace";
import { workspaceSwitchSx } from "../Dashboard/overviewPanel";

const SuperInvestorHoldingsV2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { cik } = useParams<{ cik: string }>();
  const investor = superInvestors.find((item) => item.cik === cik);
  const { data, isLoading, error, refetch } = useSuperInvestorHoldings(
    cik ?? "",
  );
  const [view, setView] = useState<"overview" | "holdings">("overview");
  const missing = isAxiosError(error) && error.response?.status === 404;
  const backTo = "/super-investors";
  if (!investor)
    return (
      <InvestorWorkspace>
        <WorkspaceHeader
          title="Investor not found"
          backTo={backTo}
          backState={{ investorSection: "super-investors" }}
          backLabel="Super investors"
        />
        <WorkspaceState message="This investor profile is unavailable." />
      </InvestorWorkspace>
    );
  return (
    <InvestorWorkspace tour="super-investor-portfolio">
      <WorkspaceHeader
        title={investor.name}
        mobileProfile
        description={investor.fund}
        backTo={backTo}
        backState={{ investorSection: "super-investors" }}
        backLabel="Super investors"
        metrics={
          !isMobile &&
          data &&
          !error &&
          data.data.length > 0 && (
            <>
              <WorkspaceMetric
                label="Quarter"
                value={`Q${data.quarter} ${data.year}`}
              />
              <WorkspaceMetric
                label="Positions"
                value={data.data.length.toLocaleString()}
              />
              <WorkspaceMetric
                label="New positions"
                value={data.quarterComparison?.summary.totalNewPositions ?? "—"}
              />
              <WorkspaceMetric
                label="Value"
                value={`$${formatNumber(data.totalPortfolioValue)}`}
                primary
              />
            </>
          )
        }
      >
        <Avatar
          {...getPersonPortraitImageProps(investor.imageUrl, 32)}
          alt=""
          sx={{ width: 32, height: 32, borderRadius: 1.5 }}
        >
          {investor.name[0]}
        </Avatar>
      </WorkspaceHeader>
      {isLoading && <WorkspaceState loading />}
      {!isLoading && error && !missing && (
        <WorkspaceState
          message="Unable to load this portfolio."
          retry={() => {
            void refetch();
          }}
        />
      )}
      {!isLoading && (missing || (!error && !data?.data.length)) && (
        <WorkspaceState message="No holding data available." />
      )}
      {!error && data && data.data.length > 0 && (
        <>
          <ToggleButtonGroup
            value={view}
            exclusive
            aria-label="Portfolio view"
            onChange={(_, next) => {
              if (next) {
                hapticSelection();
                setView(next);
              }
            }}
            sx={workspaceSwitchSx}
          >
            <ToggleButton value="overview">Portfolio overview</ToggleButton>
            <ToggleButton
              value="holdings"
              data-tour="super-investor-all-holdings-tab"
            >
              All holdings
            </ToggleButton>
          </ToggleButtonGroup>
          {view === "overview" && (
            <PortfolioDistributionChart
              data={data.portfolioDistribution}
              holdings={data.data}
              totalValue={data.totalPortfolioValue}
            />
          )}
          <CompanyHoldingsTables data={data} view={view} />
          <Typography variant="caption" color="text.secondary">
            Holdings reflect the reported quarter, rather than a live portfolio.
          </Typography>
        </>
      )}
    </InvestorWorkspace>
  );
};
export default SuperInvestorHoldingsV2;
